import React, { Component } from 'react';
import { inject, observer } from 'mobx-react';
import { merge } from 'lodash';
import lazyLoad from 'core/util/lazyLoad';
import { withRouter } from 'react-router-dom';
import Heading from 'core/components/Heading';
import { Intent } from '@blueprintjs/core';
import Flex, { FlexColumn } from 'core/components/Flex';
import Box from 'core/components/Box';
import Button from 'core/components/Button';
import PageTabs, { PageTab } from 'app/components/page/PageTabs';
import NotificationSettings from 'app/views/settings/notifications/NotificationSettings';
import { fields, options } from 'app/forms/config/notification';
import NotificationPreview from 'app/views/settings/notifications/NotificationPreview';
import NotificationUsage from 'app/views/settings/notifications/NotificationUsage';
import FormComponent from 'core/form/components/FormComponent';
import { showSuccessToast, showErrorToast } from 'core/components/toast';
import { SubmitButton } from 'core/form';
import { CHANNEL_TYPES, TEMPLATE_BASED_CHANNEL_TYPES } from '@kentik/ui-shared/notifications/constants';
import {
  TEMPLATE_KEY_CUSTOM_SUBJECT,
  TEMPLATE_KEY_CUSTOM_BODY,
  TEMPLATE_KEY_CUSTOM_TEMPLATE,
  DEFAULT_PREVIEW_DATA
} from './notificationTemplates/notificationTemplateDefaults';

const NotificationTemplates = lazyLoad(() => import('./notificationTemplates/NotificationTemplates'));

const NOTIFICATION_TABS = {
  SETTINGS: 'notification-details',
  PREVIEW: 'notification-preview',
  TEMPLATING: 'notification-templates',
  USED_BY: 'notification-used-by'
};

@inject('$notifications', '$exports')
@withRouter
@observer
export default class NotificationDetails extends Component {
  state = {
    selectedTabId: NOTIFICATION_TABS.SETTINGS,

    templateEditorState: {
      alertId: '',
      activeTemplateKey: TEMPLATE_KEY_CUSTOM_TEMPLATE,
      previewDataJson: DEFAULT_PREVIEW_DATA,
      dataSourceMode: 'custom'
    }
  };

  updateTemplateEditorState = (patch) => {
    this.setState((prev) => ({
      templateEditorState: {
        ...prev.templateEditorState,
        ...patch
      }
    }));
  };

  handleFormLoad = (form) => {
    this.form = form;
  };

  handleTabChange = (tab) => {
    const { $exports } = this.props;
    const { selectedTabId } = this.state;

    if (this.form) {
      if (selectedTabId === NOTIFICATION_TABS.SETTINGS) {
        this.form.clearFieldsPristineState();
      }
    }

    if (tab === NOTIFICATION_TABS.TEMPLATING && this.form) {
      const channelType = this.form.getValue('channelType');
      const useBody = !!this.form.getValue('config.renderingConfig.emailConfig.useBodyCustomTemplate');
      const useSubject = !!this.form.getValue('config.renderingConfig.emailConfig.useSubjectCustomTemplate');

      this.setState((prev) => {
        let nextKey = prev.templateEditorState.activeTemplateKey;

        if (channelType === CHANNEL_TYPES.EMAIL) {
          const allowed = [TEMPLATE_KEY_CUSTOM_SUBJECT, TEMPLATE_KEY_CUSTOM_BODY];

          if (!allowed.includes(nextKey)) {
            nextKey = TEMPLATE_KEY_CUSTOM_SUBJECT;
          }

          if (useSubject && !useBody) {
            nextKey = TEMPLATE_KEY_CUSTOM_SUBJECT;
          }

          if (useBody && !useSubject) {
            nextKey = TEMPLATE_KEY_CUSTOM_BODY;
          }
        } else {
          nextKey = TEMPLATE_KEY_CUSTOM_TEMPLATE;
        }

        return {
          selectedTabId: tab,
          templateEditorState: {
            ...prev.templateEditorState,
            activeTemplateKey: nextKey
          }
        };
      });

      $exports.setHash({ hashedSelectedTabId: tab });
      return;
    }

    this.setState({ selectedTabId: tab });
    $exports.setHash({ hashedSelectedTabId: tab });
  };

  handleCancel = () => {
    const { history, onClose } = this.props;

    if (onClose) {
      onClose(null);
      return;
    }

    history.goBack();
  };

  handleSubmit = (form, values) => {
    const { history, model, onClose } = this.props;

    // lots of deeply nested properties here and only some are getting updated
    model.set({
      ...values,
      config: merge({}, model.get('config'), values.config)
    });

    // allow previous merge so we don't regress on current functionality, but force email updates as merge doesn't deal with array deletions the way we expect.
    const config = model.get('config');

    if (config?.sendingConfig?.usernames) {
      config.sendingConfig.usernames = values?.config?.sendingConfig?.usernames || [];
    }
    if (config?.sendingConfig?.customHeaders) {
      config.sendingConfig.customHeaders = values?.config?.sendingConfig?.customHeaders || [];
    }

    model.set({ config });

    return model.save().then(() => {
      if (onClose) {
        onClose(model);
      } else {
        history.replace(`/v4/settings/notifications/${model.id}`);
        history.push('/v4/settings/notifications');
      }
    });
  };

  isTemplatingTabDisabled = (form) => {
    const channelType = form.getValue('channelType');

    // Disable templating tab for non-template based channel types
    if (!TEMPLATE_BASED_CHANNEL_TYPES.includes(channelType)) {
      return true;
    }

    // Disable templating tab for email channel if both custom templates are disabled
    if (channelType === CHANNEL_TYPES.EMAIL) {
      return (
        !form.getValue('config.renderingConfig.emailConfig.useBodyCustomTemplate') &&
        !form.getValue('config.renderingConfig.emailConfig.useSubjectCustomTemplate')
      );
    }

    // Otherwise, enable the tab, e.g. for custom webhook
    return false;
  };

  handleTestClick = (form) => () => {
    const { $notifications, model } = this.props;
    const { selectedTabId, templateEditorState } = this.state;
    const notificationType = form.getValue('notificationType');

    let alertModelId;
    let effectiveNotificationType = notificationType;

    if (selectedTabId === NOTIFICATION_TABS.TEMPLATING) {
      if (templateEditorState?.dataSourceMode === 'custom') {
        alertModelId = templateEditorState?.alertId;
      } else {
        effectiveNotificationType = templateEditorState?.dataSourceMode;
      }
    } else {
      alertModelId = form.getValue('alertModelId');
    }

    this.setState({ notificationTestLoading: true });

    const testPayload = {
      channel: model.serialize({ ...form.getValues(), channelID: model.get('channelID') })
    };

    const trimmedAlertModelId = alertModelId?.trim();

    if (trimmedAlertModelId) {
      testPayload.notification_event = { alertmanEvent: { alarmID: trimmedAlertModelId } };
    } else {
      testPayload.test_model = { model_type: effectiveNotificationType };
    }

    $notifications
      .testChannel(testPayload)
      .then(() => {
        showSuccessToast('Test Notification has been sent.');
      })
      .catch((err) => {
        showErrorToast(err?.message || 'Failed to send test notification.');
      })
      .finally(() => {
        this.setState({ notificationTestLoading: false });
      });
  };

  render() {
    const { selectedTabId, templateEditorState, notificationTestLoading } = this.state;
    const { model, isCreate } = this.props;

    return (
      <FormComponent fields={fields} options={options} model={model} large onLoad={this.handleFormLoad}>
        {({ form }) => (
          <FlexColumn flex="1">
            <FlexColumn pt={3}>
              <Flex mb="12px" alignItems="center" justifyContent="space-between">
                <Heading level={1} mb={0}>
                  {isCreate ? 'Add' : 'Edit'} Notification Channel
                </Heading>
                <Flex>
                  <Button
                    maxHeight="32px"
                    minWidth="100px"
                    ml={2}
                    onClick={this.handleCancel}
                    text="Cancel"
                    intent={Intent.NONE}
                  />
                  <SubmitButton maxHeight="32px" ml={2} onSubmit={this.handleSubmit} intent={Intent.PRIMARY} />
                </Flex>
              </Flex>
              <PageTabs selectedTabId={selectedTabId} onChange={this.handleTabChange} large>
                <PageTab
                  id={NOTIFICATION_TABS.SETTINGS}
                  title="Settings"
                  panel={
                    <Box maxWidth="840px" pb="300px">
                      <NotificationSettings model={model} role="tabpanel" handleTabChange={this.handleTabChange} />
                    </Box>
                  }
                />
                {this.isTemplatingTabDisabled(form) && (
                  <PageTab
                    key={1}
                    id={NOTIFICATION_TABS.PREVIEW}
                    title="Preview"
                    panel={
                      <NotificationPreview
                        model={model}
                        role="tabpanel"
                        handleTestClick={this.handleTestClick(form)}
                        notificationTestLoading={notificationTestLoading}
                      />
                    }
                  />
                )}
                {!this.isTemplatingTabDisabled(form) && (
                  <PageTab
                    key={2}
                    id={NOTIFICATION_TABS.TEMPLATING}
                    title="Template & Preview"
                    panel={
                      <NotificationTemplates
                        activeTemplateKey={templateEditorState.activeTemplateKey}
                        previewAlertId={templateEditorState.alertId}
                        previewDataJson={templateEditorState.previewDataJson}
                        dataSourceMode={templateEditorState.dataSourceMode}
                        onPreviewStateChange={this.updateTemplateEditorState}
                        handleTestClick={this.handleTestClick(form)}
                        notificationTestLoading={notificationTestLoading}
                        model={model}
                        role="tabpanel"
                      />
                    }
                  />
                )}
                {!model.isNew && (
                  <PageTab
                    key={3}
                    id={NOTIFICATION_TABS.USED_BY}
                    title="Used By"
                    panel={<NotificationUsage model={model} role="tabpanel" />}
                  />
                )}
              </PageTabs>
            </FlexColumn>
          </FlexColumn>
        )}
      </FormComponent>
    );
  }
}
